import type { ExerciseTemplate } from '../types/exercise-template'
import type { ResultRep, ResultSet, ResultTemplateBlock } from '../types/workout'

type CompletionBucket = 25 | 50 | 75 | 100

export function buildDetailsFromTemplates(templates: ExerciseTemplate[]): ResultTemplateBlock[] {
  return templates.map((tpl) => {
    const repsPerSet = Math.max(1, tpl.reps_per_set)
    const setCount = Math.max(1, tpl.sets)
    const sets: ResultSet[] = Array.from({ length: setCount }, () => ({
      reps: Array.from({ length: repsPerSet }, () => emptyRep()),
    }))
    return {
      exercise_template_id: Number(tpl.id),
      exercise_template_title: tpl.title ?? '',
      kind: tpl.kind,
      sets,
    }
  })
}

function emptyRep(): ResultRep {
  return { complete: false, weight_kg: null }
}

export function countReps(details: ResultTemplateBlock[]): { total: number; done: number } {
  let total = 0
  let done = 0
  for (const block of details) {
    for (const set of block.sets) {
      for (const rep of set.reps) {
        total += 1
        if (rep.complete) done += 1
      }
    }
  }
  return { total, done }
}

export function summarizeCompletion(details: ResultTemplateBlock[]): CompletionBucket {
  const { total, done } = countReps(details)
  if (total === 0) return 25
  const ratio = done / total
  if (ratio >= 0.875) return 100
  if (ratio >= 0.625) return 75
  if (ratio >= 0.375) return 50
  return 25
}

export type CascadeState = 'checked' | 'unchecked' | 'indeterminate'

export function templateCascadeState(block: ResultTemplateBlock): CascadeState {
  let total = 0
  let done = 0
  for (const set of block.sets) {
    for (const rep of set.reps) {
      total += 1
      if (rep.complete) done += 1
    }
  }
  if (total === 0) return 'unchecked'
  if (done === 0) return 'unchecked'
  if (done === total) return 'checked'
  return 'indeterminate'
}

export function setCascadeState(set: ResultSet): CascadeState {
  if (set.reps.length === 0) return 'unchecked'
  const done = set.reps.filter((r) => r.complete).length
  if (done === 0) return 'unchecked'
  if (done === set.reps.length) return 'checked'
  return 'indeterminate'
}

export function setTemplateComplete(
  details: ResultTemplateBlock[],
  templateIdx: number,
  complete: boolean,
): ResultTemplateBlock[] {
  return details.map((block, i) => {
    if (i !== templateIdx) return block
    return {
      ...block,
      sets: block.sets.map((set) => ({
        reps: set.reps.map((rep) => ({ ...rep, complete })),
      })),
    }
  })
}

export function setSetComplete(
  details: ResultTemplateBlock[],
  templateIdx: number,
  setIdx: number,
  complete: boolean,
): ResultTemplateBlock[] {
  return details.map((block, i) => {
    if (i !== templateIdx) return block
    return {
      ...block,
      sets: block.sets.map((set, si) =>
        si === setIdx ? { reps: set.reps.map((rep) => ({ ...rep, complete })) } : set,
      ),
    }
  })
}

export function setRepComplete(
  details: ResultTemplateBlock[],
  templateIdx: number,
  setIdx: number,
  repIdx: number,
  complete: boolean,
): ResultTemplateBlock[] {
  return details.map((block, i) => {
    if (i !== templateIdx) return block
    return {
      ...block,
      sets: block.sets.map((set, si) => {
        if (si !== setIdx) return set
        return { reps: set.reps.map((rep, ri) => (ri === repIdx ? { ...rep, complete } : rep)) }
      }),
    }
  })
}

export function applyWeightToTemplate(
  details: ResultTemplateBlock[],
  templateIdx: number,
  weight: number | null,
): ResultTemplateBlock[] {
  return details.map((block, i) => {
    if (i !== templateIdx) return block
    return {
      ...block,
      sets: block.sets.map((set) => ({
        reps: set.reps.map((rep) => ({ ...rep, weight_kg: weight })),
      })),
    }
  })
}

export function applyWeightToSet(
  details: ResultTemplateBlock[],
  templateIdx: number,
  setIdx: number,
  weight: number | null,
): ResultTemplateBlock[] {
  return details.map((block, i) => {
    if (i !== templateIdx) return block
    return {
      ...block,
      sets: block.sets.map((set, si) =>
        si === setIdx ? { reps: set.reps.map((rep) => ({ ...rep, weight_kg: weight })) } : set,
      ),
    }
  })
}

export function setRepWeight(
  details: ResultTemplateBlock[],
  templateIdx: number,
  setIdx: number,
  repIdx: number,
  weight: number | null,
): ResultTemplateBlock[] {
  return details.map((block, i) => {
    if (i !== templateIdx) return block
    return {
      ...block,
      sets: block.sets.map((set, si) => {
        if (si !== setIdx) return set
        return { reps: set.reps.map((rep, ri) => (ri === repIdx ? { ...rep, weight_kg: weight } : rep)) }
      }),
    }
  })
}

export function parseWeightInput(value: string): number | null | 'invalid' {
  const trimmed = value.trim()
  if (trimmed === '') return null
  if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) return 'invalid'
  const num = Number(trimmed)
  if (Number.isNaN(num) || num < 0 || num > 999) return 'invalid'
  return num
}
